import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Ban, Trash2, Gift, X, Pencil, Search, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import { validatePassword } from "../../lib/passwordPolicy";
import PasswordRules from "../../components/PasswordRules";
import ConfirmDialog from "../../components/ConfirmDialog";
import HistoryModal from "../../components/admin/HistoryModal";

export default function AdminCommercesManage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [grantItem, setGrantItem] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  // Spec fork 24 — historique et reset.
  const [historyItem, setHistoryItem] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/commerces");
      setItems(data || []);
    } catch { setItems([]); }
  };

  useEffect(() => { load(); }, []);

  const toggleBlock = async (c) => {
    try {
      await api.patch(`/admin/commerces/${c.id}`, { isBlocked: !c.isBlocked });
      toast.success(c.isBlocked ? "Débloqué" : "Bloqué");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/commerces/${c.id}`, { isActive: !c.isActive });
      toast.success(c.isActive ? "Suspendu" : "Réactivé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const archive = (c) => {
    setArchiveTarget(c);
  };

  const performArchive = async () => {
    const c = archiveTarget;
    setArchiveTarget(null);
    if (!c) return;
    try {
      await api.delete(`/admin/commerces/${c.id}`);
      toast.success(`Établissement supprimé. L'email ${c.email} est libéré.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!items) return <div className="text-subtle">Chargement…</div>;
  const filtered = q ? items.filter((c) => [c.name, c.email, c.city].some((s) => String(s || "").toLowerCase().includes(q.toLowerCase()))) : items;

  return (
    <div data-testid="admin-commerces-manage">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-display text-2xl font-black text-ink">Établissements</h2>
        <div className="flex gap-2">
          <Button onClick={() => setResetOpen(true)} size="sm" variant="outline" className="border-destructive text-destructive hover:bg-destructive/5 rounded-md" data-testid="commerces-reset-history-btn">
            <RotateCcw size={14} className="mr-1" /> Réinitialiser l'historique
          </Button>
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="commerces-create-btn">
            <Plus size={14} className="mr-1" /> Créer un établissement
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle" size={14} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher nom, email, ville..." className="pl-9 h-10" data-testid="commerces-search" />
      </div>

      <div className="border border-border overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-surface/60 text-subtle text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Inscription</th>
              <th className="text-left p-3">Abonnement</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-subtle">Aucun établissement</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`commerce-row-${c.id}`}>
                <td className="p-3 text-ink">{c.name}<br /><span className="text-xs text-subtle">{c.type} · {c.city}</span></td>
                <td className="p-3 text-ink font-mono text-xs">{c.email}</td>
                <td className="p-3 text-subtle">{new Date(c.createdAt).toLocaleDateString("fr-FR")}</td>
                <td className="p-3 text-xs">
                  <span className="uppercase tracking-wider font-bold">{c.subscriptionStatus}</span>
                  <br />
                  <span className="text-subtle">
                    {c.subscriptionEndDate ? `Fin : ${new Date(c.subscriptionEndDate).toLocaleDateString("fr-FR")}` : c.trialEndDate ? `Essai : ${new Date(c.trialEndDate).toLocaleDateString("fr-FR")}` : "—"}
                  </span>
                </td>
                <td className="p-3">
                  {c.isBlocked ? <Badge variant="blocked" /> : !c.isActive ? <Badge variant="suspended" /> : <Badge variant="active" />}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1 flex-wrap justify-end">
                    <IconBtn onClick={() => setHistoryItem(c)} title="Voir l'historique" icon={<History size={12} />} testid={`commerce-history-${c.id}`} />
                    <IconBtn onClick={() => setEditItem(c)} title="Modifier" icon={<Pencil size={12} />} testid={`commerce-edit-${c.id}`} />
                    <IconBtn onClick={() => setGrantItem(c)} title="Abonnement" icon={<Gift size={12} />} testid={`commerce-grant-${c.id}`} />
                    <IconBtn onClick={() => toggleActive(c)} title={c.isActive ? "Suspendre" : "Réactiver"} icon={<RotateCcw size={12} />} testid={`commerce-toggle-active-${c.id}`} />
                    <IconBtn onClick={() => toggleBlock(c)} title={c.isBlocked ? "Débloquer" : "Bloquer"} icon={<Ban size={12} className={c.isBlocked ? "text-destructive" : ""} />} testid={`commerce-block-${c.id}`} />
                    <IconBtn onClick={() => archive(c)} title="Supprimer définitivement" icon={<Trash2 size={12} className="text-destructive" />} testid={`commerce-archive-${c.id}`} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE — commerces en cards */}
      <div className="md:hidden space-y-2" data-testid="commerces-cards-mobile">
        {filtered.length === 0 && <div className="border border-border p-6 text-center text-subtle text-sm">Aucun établissement</div>}
        {filtered.map((c) => (
          <div key={c.id} className="border border-border bg-white p-3" data-testid={`commerce-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <p className="font-bold text-ink truncate">{c.name}</p>
                <p className="text-[11px] text-subtle truncate">{c.type} · {c.city}</p>
                <p className="text-xs font-mono text-subtle truncate mt-0.5">{c.email}</p>
              </div>
              {c.isBlocked ? <Badge variant="blocked" /> : !c.isActive ? <Badge variant="suspended" /> : <Badge variant="active" />}
            </div>
            <div className="text-[11px] text-subtle space-y-0.5 mb-2">
              <p>Inscrit le {new Date(c.createdAt).toLocaleDateString("fr-FR")}</p>
              <p>
                Abonnement : <span className="uppercase font-bold tracking-wider text-ink">{c.subscriptionStatus}</span>
                {" · "}
                {c.subscriptionEndDate ? `Fin : ${new Date(c.subscriptionEndDate).toLocaleDateString("fr-FR")}` : c.trialEndDate ? `Essai : ${new Date(c.trialEndDate).toLocaleDateString("fr-FR")}` : "—"}
              </p>
            </div>
            <div className="inline-flex gap-1 flex-wrap pt-2 border-t border-border w-full">
              <IconBtn onClick={() => setHistoryItem(c)} title="Voir l'historique" icon={<History size={12} />} testid={`commerce-history-m-${c.id}`} />
              <IconBtn onClick={() => setEditItem(c)} title="Modifier" icon={<Pencil size={12} />} testid={`commerce-edit-m-${c.id}`} />
              <IconBtn onClick={() => setGrantItem(c)} title="Abonnement" icon={<Gift size={12} />} testid={`commerce-grant-m-${c.id}`} />
              <IconBtn onClick={() => toggleActive(c)} title={c.isActive ? "Suspendre" : "Réactiver"} icon={<RotateCcw size={12} />} testid={`commerce-toggle-active-m-${c.id}`} />
              <IconBtn onClick={() => toggleBlock(c)} title={c.isBlocked ? "Débloquer" : "Bloquer"} icon={<Ban size={12} className={c.isBlocked ? "text-destructive" : ""} />} testid={`commerce-block-m-${c.id}`} />
              <IconBtn onClick={() => archive(c)} title="Supprimer définitivement" icon={<Trash2 size={12} className="text-destructive" />} testid={`commerce-archive-m-${c.id}`} />
            </div>
          </div>
        ))}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
      {editItem && <EditModal item={editItem} onClose={() => setEditItem(null)} onDone={() => { setEditItem(null); load(); }} />}
      {grantItem && <GrantModal item={grantItem} onClose={() => setGrantItem(null)} onDone={() => { setGrantItem(null); load(); }} />}

      <ConfirmDialog
        open={!!archiveTarget}
        title={archiveTarget ? `Supprimer « ${archiveTarget.name} » ?` : "Supprimer"}
        message={archiveTarget ? (
          <>
            <p>Toutes les files et tickets de cet établissement seront <strong>effacés</strong>.</p>
            <p>L'email <strong>{archiveTarget.email}</strong> sera libéré et pourra être réutilisé.</p>
            <p className="text-destructive font-bold">Cette action est irréversible.</p>
          </>
        ) : null}
        confirmLabel="Supprimer"
        destructive
        onConfirm={performArchive}
        onCancel={() => setArchiveTarget(null)}
        testid="commerce-archive-confirm"
      />

      {historyItem && (
        <HistoryModal
          kind="commerce"
          entityId={historyItem.id}
          title={`${historyItem.name || "—"} (${historyItem.city || "—"})`}
          onClose={() => setHistoryItem(null)}
        />
      )}

      <ConfirmDialog
        open={resetOpen}
        title="Réinitialiser l'historique des établissements ?"
        message={(
          <>
            <p>Toutes les lignes d'historique (paiements Stripe historiques, transactions confirmées/annulées) seront <strong>supprimées définitivement</strong>.</p>
            <p>Les comptes établissements et leurs files actives <strong>ne sont PAS touchés</strong>.</p>
            <p className="text-destructive font-bold">Cette action est irréversible.</p>
          </>
        )}
        confirmLabel={resetting ? "Réinitialisation…" : "Réinitialiser"}
        destructive
        onConfirm={async () => {
          setResetting(true);
          try {
            const { data } = await api.post("/admin/history/reset", { scope: "commerces" });
            toast.success(`Historique réinitialisé (${data?.deleted?.transactions || 0} transactions supprimées)`);
            setResetOpen(false);
            load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
          } finally {
            setResetting(false);
          }
        }}
        onCancel={() => setResetOpen(false)}
        testid="commerces-reset-confirm"
      />
    </div>
  );
}

function IconBtn({ onClick, title, icon, testid }) {
  return <button onClick={onClick} title={title} data-testid={testid} className="h-7 w-7 border border-border inline-flex items-center justify-center hover:border-ink bg-white">{icon}</button>;
}

function Badge({ variant }) {
  const map = {
    active: { label: "Actif", cls: "bg-[#ECFBEF] text-[#0A7F2E]" },
    blocked: { label: "Bloqué", cls: "bg-[#FDE2E2] text-destructive" },
    suspended: { label: "Suspendu", cls: "bg-[#FFFBE6] text-[#9A7D00]" },
  };
  const v = map[variant];
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${v.cls}`}>{v.label}</span>;
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className={`bg-white border border-border ${wide ? "max-w-2xl" : "max-w-md"} w-full mt-20`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-display text-lg font-bold text-ink">{title}</h3>
          <button onClick={onClose} className="text-subtle hover:text-ink"><X size={16} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    name: "", type: "Autre", address: "", city: "", postalCode: "",
    email: "", phone: "", password: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password) {
      const v = validatePassword(form.password);
      if (!v.ok) { toast.error(v.message); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/commerces", form);
      toast.success(data.generatedPassword ? `Créé. Mot de passe : ${data.generatedPassword}` : "Établissement créé", { duration: 10000 });
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Créer un établissement" onClose={onClose} wide>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="admin-commerce-create-form">
        <div className="col-span-2"><Label>Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label>Type</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} /></div>
        <div><Label>Téléphone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        <div className="col-span-2"><Label>Adresse</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        <div><Label>Ville</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
        <div><Label>Code postal</Label><Input required value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></div>
        <div className="col-span-2"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-commerce-create-email" /></div>
        <div className="col-span-2">
          <Label>Mot de passe (vide = généré)</Label>
          <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {form.password && <PasswordRules password={form.password} compact />}
        </div>
        <div className="col-span-2"><Button type="submit" disabled={saving} className="w-full h-11 bg-brand text-white rounded-md" data-testid="admin-commerce-create-submit">Créer</Button></div>
      </form>
    </Modal>
  );
}

function EditModal({ item, onClose, onDone }) {
  const [form, setForm] = useState({ name: item.name, email: item.email, phone: item.phone, address: item.address, city: item.city, postalCode: item.postalCode, password: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password) {
      const v = validatePassword(form.password);
      if (!v.ok) { toast.error(v.message); return; }
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      await api.patch(`/admin/commerces/${item.id}`, body);
      toast.success("Modifié");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Modifier l'établissement" onClose={onClose} wide>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><Label>Téléphone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        <div className="col-span-2"><Label>Adresse</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        <div><Label>Ville</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
        <div><Label>Code postal</Label><Input required value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></div>
        <div className="col-span-2">
          <Label>Nouveau mot de passe (optionnel)</Label>
          <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {form.password && <PasswordRules password={form.password} compact />}
        </div>
        <div className="col-span-2"><Button type="submit" disabled={saving} className="w-full h-11 bg-brand text-white rounded-md">Enregistrer</Button></div>
      </form>
    </Modal>
  );
}

function GrantModal({ item, onClose, onDone }) {
  const [endDate, setEndDate] = useState(item.subscriptionEndDate ? item.subscriptionEndDate.slice(0, 10) : "");
  const [sub, setSub] = useState(item.subscriptionStatus || "trial");
  const [saving, setSaving] = useState(false);
  const unlimited = () => { setEndDate("2099-12-31"); setSub("active"); };
  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        subscriptionStatus: sub,
        subscriptionEndDate: endDate ? new Date(endDate + "T23:59:59").toISOString() : null,
      };
      await api.patch(`/admin/commerces/${item.id}`, body);
      toast.success("Abonnement mis à jour");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Abonnement · ${item.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-subtle">Attribuer un abonnement gratuit (actif jusqu'à une date, ou "illimité" pour 2099).</p>
        <div><Label>Statut</Label>
          <select value={sub} onChange={(e) => setSub(e.target.value)} className="w-full h-10 border border-border px-3" data-testid="grant-sub-status">
            <option value="trial">Essai</option>
            <option value="active">Actif</option>
          </select>
        </div>
        <div><Label>Fin d'abonnement</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="grant-sub-end" /></div>
        <Button type="button" size="sm" variant="outline" onClick={unlimited}>Illimité (2099)</Button>
        <Button onClick={submit} disabled={saving} className="w-full h-11 bg-brand text-white rounded-md" data-testid="grant-sub-submit">Enregistrer</Button>
      </div>
    </Modal>
  );
}
